import dayjs from 'dayjs'

import TIME_OFFSET_BETWEEN_CREATED_AND_LAST_LOGIN, {
  getManagedPermissionCapabilities,
} from '~/features/user/constants'
import {
  EAccessLevel,
  EBaseAccess,
  ECapability,
  EFriendlyUserStatus,
  EUserStatus,
  TUser,
  type TUpdateUserPermissionsPayload,
} from '~/features/user/models'
import { EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import pathBuilder from '~/utils/api'

const USER_FROM_ORG_QUERY_KEY = 'adminUserFromOrg'

export const queryKeys = {
  users: (filters: Record<string, string | boolean | number> = {}) => [
    EServerPath.users,
    filters,
  ],
  usersInvite: () => [EServerPath.usersInvite],
  user: (userId: string) => [EServerPath.users, userId],
  // Own first element: useGetUserFromOrg's isFetching matches on queryKey[0].
  userFromOrg: (orgId: string) => [USER_FROM_ORG_QUERY_KEY, orgId],
  userCognito: (cognitoUid: string) => [
    EServerPath.users,
    'cognito',
    cognitoUid,
  ],
  apiUsersValidate: () => [EServerPath.apiUsersValidate],
  apiUsers: (filters: Record<string, string | boolean | number> = {}) => [
    EServerPath.apiUsers,
    filters,
  ],
  emailExists: (email?: string) =>
    email === undefined
      ? [EServerPath.apiUsersEmailExists]
      : [EServerPath.apiUsersEmailExists, email],
  myPermissions: (userId: string) => [
    EServerPath.apiUsersMePermissions,
    userId,
  ],
}

export function buildUserPermissionsPayload(
  baseAccess: EBaseAccess,
  capabilitiesForm: Partial<Record<ECapability, EAccessLevel | ''>>,
  hasVoipOrgFeature = true,
): TUpdateUserPermissionsPayload {
  if (baseAccess === EBaseAccess.full) {
    return { baseAccess, capabilities: [] }
  }

  const capabilities = getManagedPermissionCapabilities(
    hasVoipOrgFeature,
  ).flatMap(
    (
      capability,
    ): {
      capability: ECapability
      accessLevel: EAccessLevel
    }[] => {
      const accessLevel = capabilitiesForm[capability]
      const isGranted =
        accessLevel === EAccessLevel.read || accessLevel === EAccessLevel.edit

      return isGranted ? [{ capability, accessLevel }] : []
    },
  )

  return { baseAccess, capabilities }
}

export const queries = {
  userCognito: (cognitoUid: string = '') => ({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.userCognito(cognitoUid),
    enabled: !!cognitoUid,
    queryFn: () =>
      HttpService.get<TUser>({
        path: pathBuilder(EServerPath.userCognito, {
          cognitoUid,
        }),
      }),
  }),
}

export const renderUserStatus = (user: TUser): EFriendlyUserStatus => {
  if (user.status === EUserStatus.disabled) {
    return EFriendlyUserStatus.disabled
  }
  // Difference between created date and last login date is less than 5 seconds, we assume the user has not logged in yet
  const timeDifferenceBetweenCreatedAndLastLogin = Math.abs(
    dayjs(user.createdAt).diff(
      dayjs(user.lastLogin || user.createdAt),
      'second',
    ),
  )
  if (
    timeDifferenceBetweenCreatedAndLastLogin <=
    TIME_OFFSET_BETWEEN_CREATED_AND_LAST_LOGIN
  ) {
    return EFriendlyUserStatus.inviteSent
  }

  return EFriendlyUserStatus.active
}
